import React from 'react';
import {Box, Typography} from '@mui/material';
import {FieldInputProps} from 'formik';
import {Pot} from '../../../schema-types/Pot';
import {withdrawalFieldsType} from './withdrawalFields';
import {getSummary} from '../CreateRecommend/RecommendSourceDestination/naming';
import {isBankAccount} from '../../../utils/potsFilter';
import {CurrentAccountDetails} from './CurrentAccountDetails';
import {NewCurrentAccountDetails} from './NewCurrentAccountDetails';
import {NEW_PLAN_ID} from '../CreateRecommend/RecommendSourceDestination/constants';
import RecommendPlanForm from '../CreateRecommend/RecommendSourceDestination/RecommendPlanForm';
import {getCurrencyFloat, getCurrencyString} from '../../../utils/currencyFormatter';

export interface WithdrawalInfoCaptureProps {
  // Withdrawal (bank account linked to nominated account)
  linked?: boolean;
  // Withdrawal (bank account validated)
  validatedBank?: boolean;
  // Withdrawal (bank account needs linking)
  needsLink?: boolean;
}

const WithdrawalExtraText: React.FC<{
  pot: Pot;
  withdrawalFieldObj: FieldInputProps<withdrawalFieldsType | undefined>;
}> = ({pot, withdrawalFieldObj}) => {
  const {id, totalValue} = pot;
  if (id !== withdrawalFieldObj?.value?.destination?.id || isBankAccount(pot)) {
    return null;
  }
  return (
    <Typography>
      <strong data-testid="withdrawal-contribution-id">{` - Post Contribution: ${getCurrencyString(
        getCurrencyFloat(totalValue) +
          getCurrencyFloat(withdrawalFieldObj?.value?.source?.singleClientAmount || 0)
      )}`}</strong>
    </Typography>
  );
};

interface WithdrawalDestinationPlanFormProps {
  pot: Pot;
  withdrawalFieldObj: FieldInputProps<withdrawalFieldsType | undefined>;
  withdrawalPot?: Pot | null;
  isSelected: boolean;
  handleChange: (event: React.ChangeEvent<any> | null, value: string) => void;
}

export const WithdrawalDestinationPlanForm: React.FC<
  WithdrawalDestinationPlanFormProps
> = ({pot, withdrawalFieldObj, withdrawalPot, isSelected, handleChange}) => {
  return (
    <RecommendPlanForm
      id={pot.id}
      reccId={pot.originalOvrId ?? null}
      details={<CurrentAccountDetails currentAccount={pot} />}
      summary={
        <Box display="flex" flexDirection="row">
          {getSummary(pot)}
          &nbsp;
          <WithdrawalExtraText pot={pot} withdrawalFieldObj={withdrawalFieldObj} />
        </Box>
      }
      curoId={pot.curoId}
      withdrawalValidation={{
        needsLink: isBankAccount(pot),
        linked: withdrawalPot?.nominatedAccount?.id === pot.id,
        validatedBank: pot.isValidated ?? undefined,
      }}
      isSelected={isSelected}
      isSubplan={false}
      handleChange={handleChange}
    />
  );
};

interface WithdrawalNewAccountPlanFormProps {
  isSelected: boolean;
  handleChange: (event: React.ChangeEvent<any> | null, value: string) => void;
}

export const WithdrawalNewAccountPlanForm: React.FC<
  WithdrawalNewAccountPlanFormProps
> = ({isSelected, handleChange}) => {
  return (
    <RecommendPlanForm
      id={NEW_PLAN_ID}
      reccId={null}
      summary={<Typography>New Current Account</Typography>}
      details={<NewCurrentAccountDetails />}
      isSelected={isSelected}
      isSubplan={false}
      handleChange={handleChange}
    />
  );
};
